import { useEffect, useState } from 'react';

import { Provider } from 'react-redux';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { PersistGate } from 'redux-persist/integration/react';

import 'react-toastify/dist/ReactToastify.css';
import { PostHogSession } from './components/analytics/PostHogSession';
import {
  AuthBootstrap,
  BudgetPlanningDashboard,
  DashboardLayout,
  ErrorBoundary,
  EvaluationDetailEnhanced,
  InventoryDashboard,
  ParentDashboard,
  ProtectedRoute,
  SkipNavigation,
} from './components/appComponents';
import { AppPublicRoute } from './components/routing/AppPublicRoute';
import { MarketingCatchAllRoute } from './components/routing/MarketingCatchAllRoute';
import { MarketingContactRoute } from './components/routing/MarketingContactRoute';
import { RootHomeRoute } from './components/routing/RootHomeRoute';
import { ROUTES, APP_PERMISSIONS } from './constants';
import {
  EvaluationsPage,
  CreateEvaluationPage,
  ScoreEvaluationPage,
  EvaluationReportPage,
  PermissionSlipsPage,
  CreateFormPage,
  StudentSuccessAnalytics,
  InterventionPage,
  InterventionDashboard,
  ReportsPage,
  AnnouncementDashboard,
  AnnouncementDetailPage,
 UsersPage, SchoolsPage, DistrictsPage, SettingsPage } from './pages';
import { LoginPage, RegisterPage, UnauthorizedPage } from './pages/auth';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { parentPortalApi } from './services/parentPortalApi';
import { store, persistor } from './store';
import { cspUtils, announceToScreenReader } from './utils';
import './index.css';

// Wrapper component to extract evaluation ID from URL params
const EvaluationDetailWrapper = () => {
  const { id } = useParams<{ id: string }>();
  if (!id) {
    return <Navigate to="/evaluations" replace />;
  }
  return (
    <DashboardLayout
      title="Evaluation Details"
      subtitle="Review and manage evaluation details"
    >
      <EvaluationDetailEnhanced />
    </DashboardLayout>
  );
};

const ScoreEvaluationWrapper = () => {
  const { id } = useParams<{ id: string }>();
  if (!id) {
    return <Navigate to="/evaluations" replace />;
  }
  return (
    <DashboardLayout
      title="Score Evaluation"
      subtitle="Score the evaluation against its rubric"
    >
      <ScoreEvaluationPage />
    </DashboardLayout>
  );
};

const EvaluationReportWrapper = () => {
  const { id } = useParams<{ id: string }>();
  if (!id) {
    return <Navigate to="/evaluations" replace />;
  }
  return (
    <DashboardLayout
      title="Evaluation Report"
      subtitle="Comprehensive scored evaluation report"
    >
      <EvaluationReportPage />
    </DashboardLayout>
  );
};

// Page wrapper components that include the DashboardLayout
const AnalyticsPageWrapper = () => (
  <DashboardLayout
    title="Analytics"
    subtitle="Live evaluation analytics for your district"
  >
    <StudentSuccessAnalytics />
  </DashboardLayout>
);

const InterventionsPageWrapper = () => (
  <DashboardLayout
    title="Interventions"
    subtitle="Student intervention tracking and management"
  >
    <InterventionPage />
  </DashboardLayout>
);

const InterventionsDashboardWrapper = () => (
  <DashboardLayout
    title="Intervention Dashboard"
    subtitle="Overview of intervention programs"
  >
    <InterventionDashboard />
  </DashboardLayout>
);

const ReportsPageWrapper = () => (
  <DashboardLayout
    title="Reports & Analytics"
    subtitle="Comprehensive reporting and analytics dashboard"
  >
    <ReportsPage />
  </DashboardLayout>
);

const BudgetPageWrapper = () => (
  <DashboardLayout
    title="Budget Planning"
    subtitle="Financial planning and reporting tools"
  >
    <BudgetPlanningDashboard />
  </DashboardLayout>
);

const InventoryPageWrapper = () => (
  <DashboardLayout
    title="Inventory Management"
    subtitle="School supplies and equipment tracking"
  >
    <InventoryDashboard />
  </DashboardLayout>
);

const AnnouncementsPageWrapper = () => (
  <DashboardLayout
    title="Announcements"
    subtitle="School-wide announcements and communications"
  >
    <AnnouncementDashboard />
  </DashboardLayout>
);

// Wrapper component to extract announcement ID from URL params
const AnnouncementDetailWrapper = () => {
  const { id } = useParams<{ id: string }>();
  if (!id) {
    return <Navigate to="/announcements" replace />;
  }
  return (
    <DashboardLayout
      title="Announcement Details"
      subtitle="View and manage announcement details"
    >
      <AnnouncementDetailPage />
    </DashboardLayout>
  );
};

const ParentPortalWrapper = () => {
  const [students, setStudents] = useState<
    { id: number; name: string; grade: string; school: string }[]
  >([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [studentsError, setStudentsError] = useState<string | null>(null);

  useEffect(() => {
    parentPortalApi
      .listLinkedStudents()
      .then(options => {
        const mapped = options.map(o => ({
          id: o.id,
          name: o.name,
          grade: o.grade_level ? `Grade ${o.grade_level}` : 'Grade N/A',
          school: o.school_name || 'Unknown school',
        }));
        setStudents(mapped);
        setSelectedId(mapped[0]?.id ?? null);
      })
      .catch(err =>
        setStudentsError(
          err instanceof Error ? err.message : 'Failed to load students'
        )
      )
      .finally(() => setStudentsLoading(false));
  }, []);

  const selectedStudent = students.find(s => s.id === selectedId) ?? null;

  return (
    <DashboardLayout
      title="Parent Portal"
      subtitle="Student insights and teacher collaboration"
    >
      {studentsLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          <span className="ml-3 text-gray-600">Loading students...</span>
        </div>
      ) : studentsError ? (
        <div className="p-6 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {studentsError}
        </div>
      ) : !selectedStudent ? (
        <div className="p-10 text-center text-gray-600 max-w-lg mx-auto">
          <p className="font-medium text-gray-900 mb-2">No linked students</p>
          <p>
            Your account does not have any students assigned yet. A school
            administrator can link students to your parent profile.
          </p>
        </div>
      ) : (
        <ParentDashboard
          student={selectedStudent}
          availableStudents={students}
          onStudentChange={setSelectedId}
        />
      )}
    </DashboardLayout>
  );
};

// Evaluations wrappers
const EvaluationsPageWrapper = () => (
  <DashboardLayout
    title="Evaluations"
    subtitle="Create, view, and manage evaluations"
  >
    <EvaluationsPage />
  </DashboardLayout>
);

const CreateEvaluationPageWrapper = () => (
  <DashboardLayout title="Create Evaluation" subtitle="Start a new evaluation">
    <CreateEvaluationPage />
  </DashboardLayout>
);

// Permission slips wrappers
const PermissionSlipsPageWrapper = () => (
  <DashboardLayout
    title="Permission Slips"
    subtitle="Manage and track permission slips"
  >
    <PermissionSlipsPage />
  </DashboardLayout>
);

const CreatePermissionSlipWrapper = () => (
  <DashboardLayout
    title="Create Permission Slip"
    subtitle="Build a new permission slip form"
  >
    <CreateFormPage />
  </DashboardLayout>
);

// Placeholder components for pages that don't exist yet
const UsersPageWrapper = () => (
  <DashboardLayout
    title="Users Management"
    subtitle="Manage teachers, administrators, and evaluators"
  >
    <UsersPage />
  </DashboardLayout>
);

const SchoolsPageWrapper = () => (
  <DashboardLayout
    title="Schools Management"
    subtitle="Manage schools, campuses, and related data"
  >
    <SchoolsPage />
  </DashboardLayout>
);

// New: Districts wrapper
const DistrictsPageWrapper = () => (
  <DashboardLayout
    title="Districts Management"
    subtitle="Manage districts and related data"
  >
    <DistrictsPage />
  </DashboardLayout>
);

const SettingsPageWrapper = () => (
  <DashboardLayout
    title="Settings"
    subtitle="System configuration and preferences"
  >
    <SettingsPage />
  </DashboardLayout>
);

function App() {
  useEffect(() => {
    // Set CSP headers on app initialization
    cspUtils.setCSPMeta();

    // Announce app load to screen readers
    announceToScreenReader('K-12 Admin Assistant Platform loaded successfully');

    // Set document title for accessibility
    document.title = 'K-12 Admin Assistant Platform';

    // Set language attribute
    document.documentElement.lang = 'en';

    // Add viewport meta tag if not present
    if (!document.querySelector('meta[name="viewport"]')) {
      const viewport = document.createElement('meta');
      viewport.name = 'viewport';
      viewport.content = 'width=device-width, initial-scale=1.0';
      document.head.appendChild(viewport);
    }

    // Add theme color meta tag
    if (!document.querySelector('meta[name="theme-color"]')) {
      const themeColor = document.createElement('meta');
      themeColor.name = 'theme-color';
      themeColor.content = '#3B82F6'; // Primary blue color
      document.head.appendChild(themeColor);
    }
  }, []);

  return (
    <Provider store={store}>
      <PersistGate loading={<div>Loading...</div>} persistor={persistor}>
        <PostHogSession />
        <ErrorBoundary>
            <AuthBootstrap />
          <SkipNavigation />
          <Router>
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />
            <Routes>
              {/* Public routes (only accessible when not authenticated) */}
              <Route
                path={ROUTES.LOGIN}
                element={
                  <AppPublicRoute>
                    <LoginPage />
                  </AppPublicRoute>
                }
              />
              <Route
                path={ROUTES.REGISTER}
                element={
                  <AppPublicRoute>
                    <RegisterPage />
                  </AppPublicRoute>
                }
              />
              <Route
                path={ROUTES.UNAUTHORIZED}
                element={<UnauthorizedPage />}
              />

              <Route path={ROUTES.HOME} element={<RootHomeRoute />} />
              <Route path={ROUTES.CONTACT} element={<MarketingContactRoute />} />

              {/* Protected routes */}
              <Route
                path={ROUTES.DASHBOARD}
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />

              {/* Evaluations */}
              <Route
                path={ROUTES.EVALUATIONS}
                element={
                  <ProtectedRoute
                    requiredPermissions={[
                      APP_PERMISSIONS.CREATE_EVALUATION,
                      APP_PERMISSIONS.READ_EVALUATION,
                      APP_PERMISSIONS.UPDATE_EVALUATION,
                      APP_PERMISSIONS.DELETE_EVALUATION,
                      APP_PERMISSIONS.CONDUCT_EVALUATION,
                    ]}
                  >
                    <EvaluationsPageWrapper />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.EVALUATIONS_CREATE}
                element={
                  <ProtectedRoute
                    requiredPermissions={[APP_PERMISSIONS.CREATE_EVALUATION]}
                  >
                    <CreateEvaluationPageWrapper />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.EVALUATION_DETAIL}
                element={
                  <ProtectedRoute
                    requiredPermissions={[APP_PERMISSIONS.READ_EVALUATION]}
                  >
                    <EvaluationDetailWrapper />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.EVALUATION_SCORE}
                element={
                  <ProtectedRoute
                    requiredPermissions={[
                      APP_PERMISSIONS.UPDATE_EVALUATION,
                      APP_PERMISSIONS.CONDUCT_EVALUATION,
                    ]}
                  >
                    <ScoreEvaluationWrapper />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.EVALUATION_REPORT}
                element={
                  <ProtectedRoute
                    requiredPermissions={[APP_PERMISSIONS.READ_EVALUATION]}
                  >
                    <EvaluationReportWrapper />
                  </ProtectedRoute>
                }
              />

              {/* Permission Slips */}
              <Route
                path={`${ROUTES.PERMISSION_SLIPS}/*`}
                element={
                  <ProtectedRoute
                    requiredPermissions={[
                      APP_PERMISSIONS.CREATE_PERMISSION_SLIP,
                      APP_PERMISSIONS.READ_PERMISSION_SLIP,
                      APP_PERMISSIONS.UPDATE_PERMISSION_SLIP,
                      APP_PERMISSIONS.DELETE_PERMISSION_SLIP,
                    ]}
                  >
                    <PermissionSlipsPageWrapper />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.PERMISSION_SLIPS_CREATE}
                element={
                  <ProtectedRoute
                    requiredPermissions={[
                      APP_PERMISSIONS.CREATE_PERMISSION_SLIP,
                    ]}
                  >
                    <CreatePermissionSlipWrapper />
                  </ProtectedRoute>
                }
              />

              {/* Analytics */}
              <Route
                path={ROUTES.ANALYTICS}
                element={
                  <ProtectedRoute
                    requiredPermissions={[
                      APP_PERMISSIONS.VIEW_ANALYTICS,
                      APP_PERMISSIONS.VIEW_SCHOOL_ANALYTICS,
                      APP_PERMISSIONS.VIEW_DISTRICT_ANALYTICS,
                      APP_PERMISSIONS.VIEW_STATE_ANALYTICS,
                      APP_PERMISSIONS.EXPORT_REPORTS,
                    ]}
                  >
                    <AnalyticsPageWrapper />
                  </ProtectedRoute>
                }
              />

              {/* Interventions */}
              <Route
                path={ROUTES.INTERVENTIONS}
                element={
                  <ProtectedRoute
                    requiredPermissions={[
                      APP_PERMISSIONS.CREATE_INTERVENTION,
                      APP_PERMISSIONS.READ_INTERVENTION,
                      APP_PERMISSIONS.UPDATE_INTERVENTION,
                      APP_PERMISSIONS.DELETE_INTERVENTION,
                    ]}
                  >
                    <InterventionsPageWrapper />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.INTERVENTIONS_DASHBOARD}
                element={
                  <ProtectedRoute
                    requiredPermissions={[APP_PERMISSIONS.READ_INTERVENTION]}
                  >
                    <InterventionsDashboardWrapper />
                  </ProtectedRoute>
                }
              />

              {/* Other functional pages */}
              <Route
                path={ROUTES.REPORTS}
                element={
                  <ProtectedRoute
                    requiredPermissions={[
                      APP_PERMISSIONS.VIEW_ANALYTICS,
                      APP_PERMISSIONS.VIEW_SCHOOL_ANALYTICS,
                      APP_PERMISSIONS.VIEW_DISTRICT_ANALYTICS,
                      APP_PERMISSIONS.VIEW_STATE_ANALYTICS,
                      APP_PERMISSIONS.EXPORT_REPORTS,
                    ]}
                  >
                    <ReportsPageWrapper />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.BUDGET}
                element={
                  <ProtectedRoute
                    requiredPermissions={[
                      APP_PERMISSIONS.VIEW_BUDGET,
                      APP_PERMISSIONS.APPROVE_EXPENSES,
                    ]}
                  >
                    <BudgetPageWrapper />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.INVENTORY}
                element={
                  <ProtectedRoute
                    requiredPermissions={[APP_PERMISSIONS.MANAGE_RESOURCES]}
                  >
                    <InventoryPageWrapper />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.ANNOUNCEMENTS}
                element={
                  <ProtectedRoute
                    requiredPermissions={[
                      APP_PERMISSIONS.SEND_ANNOUNCEMENTS,
                      APP_PERMISSIONS.MANAGE_COMMUNICATIONS,
                    ]}
                  >
                    <AnnouncementsPageWrapper />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.ANNOUNCEMENT_DETAIL}
                element={
                  <ProtectedRoute
                    requiredPermissions={[
                      APP_PERMISSIONS.SEND_ANNOUNCEMENTS,
                      APP_PERMISSIONS.MANAGE_COMMUNICATIONS,
                    ]}
                  >
                    <AnnouncementDetailWrapper />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.PARENT_PORTAL}
                element={
                  <ProtectedRoute
                    requiredPermissions={[
                      APP_PERMISSIONS.CREATE_PARENT_PORTAL,
                      APP_PERMISSIONS.READ_PARENT_PORTAL,
                      APP_PERMISSIONS.UPDATE_PARENT_PORTAL,
                      APP_PERMISSIONS.DELETE_PARENT_PORTAL,
                    ]}
                  >
                    <ParentPortalWrapper />
                  </ProtectedRoute>
                }
              />

              {/* Placeholder pages */}
              <Route
                path={ROUTES.USERS}
                element={
                  <ProtectedRoute
                    requiredPermissions={[
                      APP_PERMISSIONS.CREATE_USER,
                      APP_PERMISSIONS.READ_USER,
                      APP_PERMISSIONS.UPDATE_USER,
                      APP_PERMISSIONS.DELETE_USER,
                    ]}
                  >
                    <UsersPageWrapper />
                  </ProtectedRoute>
                }
              />
              {/* New: Districts routes */}
              <Route
                path={ROUTES.DISTRICTS}
                element={
                  <ProtectedRoute
                    requiredPermissions={[
                      APP_PERMISSIONS.CREATE_DISTRICT,
                      APP_PERMISSIONS.READ_DISTRICT,
                      APP_PERMISSIONS.UPDATE_DISTRICT,
                      APP_PERMISSIONS.DELETE_DISTRICT,
                    ]}
                  >
                    <DistrictsPageWrapper />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.DISTRICT_DETAIL}
                element={
                  <ProtectedRoute
                    requiredPermissions={[APP_PERMISSIONS.READ_DISTRICT]}
                  >
                    <DistrictsPageWrapper />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.SCHOOLS}
                element={
                  <ProtectedRoute
                    requiredPermissions={[
                      APP_PERMISSIONS.CREATE_SCHOOL,
                      APP_PERMISSIONS.READ_SCHOOL,
                      APP_PERMISSIONS.UPDATE_SCHOOL,
                      APP_PERMISSIONS.DELETE_SCHOOL,
                    ]}
                  >
                    <SchoolsPageWrapper />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.SETTINGS}
                element={
                  <ProtectedRoute
                    requiredPermissions={[
                      APP_PERMISSIONS.MANAGE_SYSTEM_SETTINGS,
                    ]}
                  >
                    <SettingsPageWrapper />
                  </ProtectedRoute>
                }
              />

              <Route path="*" element={<MarketingCatchAllRoute />} />
            </Routes>
          </Router>
          </ErrorBoundary>
      </PersistGate>
    </Provider>
  );
}

export default App;
